import React, { useState, useEffect, useCallback } from 'react';
import { org } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Shield, UserPlus, Trash2, Users, Crown, Pencil, Eye } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = {
  admin: { label: 'Admin', icon: Crown, color: '#FF6B35', desc: 'Accès total : tournois, finances et gestion des comptes.' },
  editor: { label: 'Éditeur', icon: Pencil, color: '#4ECDC4', desc: 'Créer et gérer les tournois (pas les finances ni les comptes).' },
  reader: { label: 'Lecteur', icon: Eye, color: '#7B68EE', desc: 'Lecture seule.' },
};

const initials = (u) => ((u?.pseudo || u?.name || u?.email || '?').trim()[0] || '?').toUpperCase();

export default function RolesPage() {
  const [members, setMembers] = useState([]);
  const [myRole, setMyRole] = useState('reader');
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [newRole, setNewRole] = useState('editor');
  const [adding, setAdding] = useState(false);

  const isAdmin = myRole === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await org.getMembers();
      setMembers(res.data.members || []);
      setMyRole(res.data.my_role || 'reader');
    } catch {
      toast.error('Erreur de chargement des comptes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!email.trim()) { toast.error('Saisissez un email'); return; }
    setAdding(true);
    try {
      await org.addMember({ email: email.trim(), role: newRole });
      toast.success('Compte rattaché à la boutique');
      setEmail('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'ajouter ce compte");
    } finally {
      setAdding(false);
    }
  };

  const handleRoleChange = async (member, role) => {
    try {
      await org.updateMember(member.id, { role });
      toast.success('Rôle mis à jour');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleRemove = async (member) => {
    if (!window.confirm(`Retirer ${member.user?.pseudo || member.user?.email} de la boutique ?`)) return;
    try {
      await org.removeMember(member.id);
      toast.success('Compte retiré');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="roles-page">
      <div className="flex items-center gap-3">
        <Users className="w-7 h-7 text-[#FF6B35]" />
        <div>
          <h1 className="text-2xl font-bold text-white">Comptes & rôles</h1>
          <p className="text-[#666] text-sm">Gérez les accès à votre boutique.</p>
        </div>
      </div>

      {/* Role legend */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {Object.entries(ROLES).map(([key, r]) => {
          const Icon = r.icon;
          return (
            <div key={key} className="bg-[#1a1a1a] border border-[#333] rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4" style={{ color: r.color }} />
                <span className="text-white text-sm font-semibold">{r.label}</span>
              </div>
              <p className="text-[#666] text-xs">{r.desc}</p>
            </div>
          );
        })}
      </div>

      {/* Add member (admins only) */}
      {isAdmin && (
        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 space-y-3" data-testid="add-member-card">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#FF6B35]" />
            <h2 className="text-white font-semibold">Rattacher un compte</h2>
          </div>
          <p className="text-xs text-[#666]">Saisissez l'email d'un compte Arcanapp existant pour l'ajouter à votre boutique.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemple.com" type="email"
              className="bg-[#252525] border-[#333] text-white flex-1" data-testid="member-email-input" />
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger className="bg-[#252525] border-[#333] text-white w-full sm:w-40" data-testid="member-role-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-[#333] text-white">
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="editor">Éditeur</SelectItem>
                <SelectItem value="reader">Lecteur</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleAdd} disabled={adding} className="bg-[#FF6B35] hover:bg-[#E55A25] gap-1.5" data-testid="add-member-btn">
              <UserPlus className="w-4 h-4" />Ajouter
            </Button>
          </div>
        </div>
      )}

      {/* Members list */}
      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 space-y-2">
        <h2 className="text-white font-semibold mb-2">Comptes de la boutique ({members.length})</h2>
        {loading ? (
          <div className="flex items-center justify-center h-24"><div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#FF6B35]" /></div>
        ) : members.length === 0 ? (
          <p className="text-[#666] text-sm py-4 text-center">Aucun compte</p>
        ) : (
          members.map((m) => {
            const role = ROLES[m.role] || ROLES.reader;
            const RoleIcon = role.icon;
            return (
              <div key={m.id} className="flex items-center gap-3 bg-[#252525] rounded-lg px-3 py-2.5" data-testid={`member-row-${m.user_id}`}>
                <div className="w-9 h-9 rounded-full bg-[#FF6B35]/20 text-[#FF6B35] flex items-center justify-center font-bold text-sm shrink-0">
                  {initials(m.user)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-medium truncate">{m.user?.pseudo || m.user?.name || m.user?.email}</span>
                    {m.is_owner && <Badge className="bg-[#FF6B35]/15 text-[#FF6B35] text-[10px] gap-1"><Crown className="w-3 h-3" />Propriétaire</Badge>}
                  </div>
                  <p className="text-[#666] text-xs truncate">{m.user?.email}</p>
                </div>
                {m.is_owner ? (
                  <Badge className="text-[10px] gap-1 shrink-0" style={{ backgroundColor: `${role.color}22`, color: role.color }}>
                    <RoleIcon className="w-3 h-3" />Admin
                  </Badge>
                ) : isAdmin ? (
                  <>
                    <Select value={m.role} onValueChange={(v) => handleRoleChange(m, v)}>
                      <SelectTrigger className="bg-[#1a1a1a] border-[#333] text-white w-32 h-8 text-xs shrink-0" data-testid={`role-select-${m.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-[#333] text-white">
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="editor">Éditeur</SelectItem>
                        <SelectItem value="reader">Lecteur</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={() => handleRemove(m)} className="text-[#666] hover:text-red-400 hover:bg-red-400/10 h-8 w-8 shrink-0" data-testid={`remove-member-${m.user_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <Badge className="text-[10px] gap-1 shrink-0" style={{ backgroundColor: `${role.color}22`, color: role.color }}>
                    <RoleIcon className="w-3 h-3" />{role.label}
                  </Badge>
                )}
              </div>
            );
          })
        )}
      </div>

      {!isAdmin && !loading && (
        <p className="text-xs text-[#666] flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" />Seuls les administrateurs peuvent gérer les comptes.</p>
      )}
    </div>
  );
}
